import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Building, ExternalLink } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { fmt, tooltipStyle } from "./oversiktConstants";

export const BOLIGTYPER = [
  { key: "alle", label: "Alle bustadtypar", color: "#1F2A36" },
  { key: "eneboliger", label: "Einebustader", color: "#103766" },
  { key: "smahus", label: "Småhus", color: "#3F8A86" },
  { key: "blokkleiligheter", label: "Blokkleilegheiter", color: "#D35E4E" },
];

// data: { kommune: [...], fylke: [...], land: [...], fylke_name }
const BoligpriserSection = ({ data }) => {
  const rows = data?.kommune || [];
  const latest = rows[rows.length - 1];
  const fylkeLatest = (data?.fylke || []).find((r) => r.period === latest?.period);
  const landLatest = (data?.land || []).find((r) => r.period === latest?.period);
  const landByPeriod = Object.fromEntries((data?.land || []).map((r) => [r.period, r]));
  const periodLabel = (p) => (p ? `${p.slice(0, 4)} K${p.slice(-1)}` : "");

  const chartData = rows.map((r) => {
    const point = { period: periodLabel(r.period) };
    BOLIGTYPER.forEach((t) => {
      point[t.label] = r[t.key]?.kvm_pris ?? null;
      point[`${t.label} – landet`] = landByPeriod[r.period]?.[t.key]?.kvm_pris ?? null;
    });
    return point;
  });

  const totalSales = latest?.alle?.omsetninger ?? 0;

  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8" data-testid="boligpriser-section" id="boligpriser">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
          <Building className="w-5 h-5 text-[#103766]" />
          Bustadprisar – omsatte bustader
        </CardTitle>
        <p className="text-sm text-[#4B647F]">Gjennomsnittleg kvadratmeterpris (kr) og tal på omsetningar per bustadtype, per kvartal</p>
      </CardHeader>
      <CardContent className="pt-6">
        {!latest ? (
          <div className="h-[140px] flex items-center justify-center text-[#8AA4BF]" data-testid="boligpriser-empty">
            Bustadprisdata blir henta frå SSB – last sidan på nytt om litt
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
            <div className="xl:col-span-2 space-y-4">
              <div className="grid grid-cols-1 gap-3">
                {BOLIGTYPER.map((t) => (
                  <div key={t.key} className="p-3 bg-[#F6F8FB] rounded-lg flex items-center justify-between" data-testid={`bustadpris-${t.key}`}>
                    <div>
                      <p className="text-xs text-[#8AA4BF] flex items-center gap-2">
                        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: t.color }} />
                        {t.label} {periodLabel(latest.period)}
                      </p>
                      <p className="text-2xl font-light text-[#1F2A36]">{latest[t.key]?.kvm_pris ? `${fmt(latest[t.key].kvm_pris)} kr/m²` : "—"}</p>
                    </div>
                    <p className="text-xs text-[#4B647F] text-right">
                      {latest[t.key]?.omsetninger != null && <>{fmt(latest[t.key].omsetninger)} salg<br /></>}
                      {landLatest?.[t.key]?.kvm_pris && <>Landet: {fmt(landLatest[t.key].kvm_pris)}</>}
                    </p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-[#4B647F]">Totalt {fmt(totalSales)} bustadomsetningar i {periodLabel(latest.period)}.</p>
            </div>
            <div className="xl:col-span-3 space-y-6">
              <div className="h-[280px]" data-testid="boligpriser-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                    <XAxis dataKey="period" stroke="#8AA4BF" fontSize={12} />
                    <YAxis stroke="#8AA4BF" fontSize={12} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => `${fmt(v)} kr/m²`} contentStyle={tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {BOLIGTYPER.map((t) => (
                      <Line key={t.key} type="monotone" dataKey={t.label} stroke={t.color} strokeWidth={2} dot={{ fill: t.color, r: 3 }} connectNulls />
                    ))}
                    {BOLIGTYPER.map((t) => (
                      <Line key={`${t.key}-land`} type="monotone" dataKey={`${t.label} – landet`} stroke={t.color} strokeWidth={1.5} strokeDasharray="4 4" dot={false} connectNulls />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <Table data-testid="boligpriser-table">
                <TableHeader>
                  <TableRow className="bg-[#F6F8FB]">
                    <TableHead className="text-[#4B647F]">Kvadratmeterpris {periodLabel(latest.period)}</TableHead>
                    <TableHead className="text-[#4B647F] text-right">Kommunen</TableHead>
                    {data.fylke?.length > 0 && (
                      <TableHead className="text-[#4B647F] text-right">{data.fylke_name ? `Snitt ${data.fylke_name}` : "Fylket"}</TableHead>
                    )}
                    <TableHead className="text-[#4B647F] text-right">Landet</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {BOLIGTYPER.map((t) => (
                    <TableRow key={t.key} className="hover:bg-[#F6F8FB]/50">
                      <TableCell className="text-sm">{t.label}</TableCell>
                      <TableCell className="text-right text-sm tabular-nums font-medium">{fmt(latest[t.key]?.kvm_pris)}</TableCell>
                      {data.fylke?.length > 0 && (
                        <TableCell className="text-right text-sm tabular-nums">{fmt(fylkeLatest?.[t.key]?.kvm_pris)}</TableCell>
                      )}
                      <TableCell className="text-right text-sm tabular-nums">{fmt(landLatest?.[t.key]?.kvm_pris)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
        <p className="text-xs text-[#8AA4BF] mt-4 text-center">
          Kjelde:{" "}
          <a href="https://www.ssb.no/statbank/table/14310" target="_blank" rel="noopener noreferrer" className="text-[#103766] hover:underline inline-flex items-center gap-1">
            SSB 14310 <ExternalLink className="w-3 h-3" />
          </a>
          . Kvartalstal. Stipla linjer viser landet. Kommunar med få omsetningar kan ha store svingingar frå kvartal til kvartal.
        </p>
      </CardContent>
    </Card>
  );
};

export default BoligpriserSection;
